import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { createRef } from 'react';
import CopyToClipboardButton from './CopyToClipboardButton.js';
import EditableFieldWithWarnings from './EditableFieldWithWarnings.js';
import { buildArraySchemaWarningPath, buildFieldPaths, chronologyTreatmentStartSortMs, renderTreatmentDates } from './PathUtils.js';

export default function TimelineView({ caseInfo, setCaseInfo, fileNamesToFilePropertiesMap, displayedDocument, setDisplayedDocument, setSnackBarMessage }) {
    const tableRef = createRef();
    var documentKeysToDisplay = caseInfo !== null && 'documents' in caseInfo ?
        Object.keys(caseInfo['documents']).filter(fileKey => fileNamesToFilePropertiesMap[caseInfo['documents'][fileKey]['filename']]['selected']) :
        [];

    // Collect timeline items
    var timelineItems = documentKeysToDisplay.map(documentKey => {
        const docInfo = caseInfo['documents'][documentKey];
        docInfo['analysis']?.['timeline']?.forEach((timelineItem, i) => {
            timelineItem['id'] = documentKey + '_timeline_' + i; // Used in UI as keys for use by React framework
            timelineItem['source_filekey'] = documentKey; // File used by SourceDocumentViewer
            timelineItem['itemType'] = 'timeline'; // Mark as timeline item
        });
        return docInfo['analysis']?.['timeline'];
    })
        .filter(timelineArr => timelineArr)
        .reduce((accumulator, timelineArr) => accumulator.concat(timelineArr), [])
        .map(item => ({ ...item, sortDate: item.date })); // Add sortDate for unified sorting

    // Collect medical_chronology items
    var medicalChronologyItems = documentKeysToDisplay.map(documentKey => {
        const docInfo = caseInfo['documents'][documentKey];
        docInfo['analysis']?.['medical_chronology']?.forEach((chronologyItem, i) => {
            // Add null check to prevent errors
            if (chronologyItem) {
                chronologyItem['id'] = documentKey + '_medical_' + i; // Used in UI as keys for use by React framework
                chronologyItem['source_filekey'] = documentKey; // File used by SourceDocumentViewer
                chronologyItem['itemType'] = 'medical_chronology'; // Mark as medical_chronology item
                chronologyItem['sortDate'] = chronologyItem.treatment_start_date || chronologyItem.treatment_date || ''; // Add sortDate for unified sorting
            }
        });
        return docInfo['analysis']?.['medical_chronology'];
    })
        .filter(chronologyArr => chronologyArr) // the ?. above can cause null entries; filter them
        .reduce((accumulator, chronologyArr) => accumulator.concat(chronologyArr), [])
        .filter(chronologyItem => chronologyItem && (chronologyItem['sortDate'] || chronologyItem['summary'] || chronologyItem['providers']?.length > 0)); // Filter out invalid items

    // Combine and sort all items by date (chronology uses optional treatment_start_time for same-day ordering)
    var allItems = [...timelineItems, ...medicalChronologyItems]
        .sort((event1, event2) => {
            const date1 = event1.itemType === 'medical_chronology'
                ? chronologyTreatmentStartSortMs(event1)
                : Date.parse(event1.sortDate || '');
            const date2 = event2.itemType === 'medical_chronology'
                ? chronologyTreatmentStartSortMs(event2)
                : Date.parse(event2.sortDate || '');
            // Handle invalid dates - put them at the end
            if (isNaN(date1) && isNaN(date2)) return 0;
            if (isNaN(date1)) return 1;
            if (isNaN(date2)) return -1;
            return date1 - date2;
        });

    if (allItems.length === 0) {
        return (
            <Box sx={{
                p: 3,
                textAlign: 'center',
                mt: 8,
                mb: 8
            }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: 'var(--text-secondary)',
                        fontWeight: 400,
                        fontSize: '1.1rem',
                        lineHeight: 1.5
                    }}
                >
                    Timeline events will be automatically extracted from uploaded documents and displayed here.
                </Typography>
            </Box>
        )
    }

    var tableBodyRows = allItems
        .map(item => {
            const fileKey = item.source_filekey;
            const docInfo = caseInfo.documents[fileKey];
            const filename = docInfo?.['filename'];

            // Handle timeline items
            if (item.itemType === 'timeline') {
                const itemIndex = caseInfo.documents[fileKey]?.analysis?.timeline?.findIndex(timelineItem => timelineItem.id === item.id) || 0;
                return (
                    <TableRow
                        key={item['id']}
                        className={item['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': fileKey,
                                        'page': item.page || item['source']?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(fileKey, 'timeline', itemIndex, 'date')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <EditableFieldWithWarnings
                                onWarningClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': fileKey,
                                        'page': item.page || item['source']?.page || 1
                                    });
                                }}
                                caseInfo={caseInfo}
                                setCaseInfo={setCaseInfo}
                                {...buildFieldPaths(fileKey, 'timeline', itemIndex, 'event')}
                                setSnackBarMessage={setSnackBarMessage}
                            />
                        </TableCell>
                        <TableCell sx={{
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        fontSize: '0.9rem',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({
                                            'key': item['source_filekey'],
                                            'page': item.page || item['source']?.page || 1
                                        });
                                    }}
                                >
                                    {filename} {(item.page || item['source']?.page) && `(page ${item.page || item['source']?.page})`}{item.source?.bates_number && `(${item.source?.bates_number})`}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({
                                            'key': item['source_filekey'],
                                            'page': item.page || item['source']?.page || 1
                                        });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                );
            }

            // Handle medical_chronology items
            if (item.itemType === 'medical_chronology') {
                const itemIndex = caseInfo.documents[fileKey]?.analysis?.medical_chronology?.findIndex(chronologyItem => chronologyItem.id === item.id) || 0;
                const sourcePage = item.source?.page || item.page || 1;

                return (
                    <TableRow
                        key={item['id']}
                        className={item['source_filekey'] === displayedDocument?.['key'] ? "selected" : ""}
                        sx={{
                            '&:hover .source-view-icon': {
                                visibility: 'visible',
                                opacity: 0.7
                            }
                        }}>
                        <TableCell sx={{
                            color: 'var(--text-secondary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            {renderTreatmentDates(
                                caseInfo,
                                setCaseInfo,
                                `$.documents['${fileKey}'].analysis.medical_chronology[${itemIndex}]`,
                                setSnackBarMessage,
                                setDisplayedDocument,
                                fileKey
                            )}
                        </TableCell>
                        <TableCell sx={{
                            color: 'var(--text-primary)',
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <Box>
                                {/* Summary - current contents */}
                                <Box sx={{ mb: item.patient_name ? 0.5 : 0 }}>
                                    <EditableFieldWithWarnings
                                        onWarningClick={(e) => {
                                            e.stopPropagation();
                                            setDisplayedDocument({
                                                'key': fileKey,
                                                'page': sourcePage
                                            });
                                        }}
                                        caseInfo={caseInfo}
                                        setCaseInfo={setCaseInfo}
                                        {...buildFieldPaths(fileKey, 'medical_chronology', itemIndex, 'summary')}
                                        setSnackBarMessage={setSnackBarMessage}
                                    />
                                </Box>

                                {/* Patient name */}
                                {item.patient_name && (
                                    <Box sx={{ mb: 0.5 }}>
                                        <Typography component="span" sx={{ fontWeight: 600 }}>Patient name: </Typography>
                                        <EditableFieldWithWarnings
                                            onWarningClick={(e) => {
                                                e.stopPropagation();
                                                setDisplayedDocument({
                                                    'key': fileKey,
                                                    'page': sourcePage
                                                });
                                            }}
                                            caseInfo={caseInfo}
                                            setCaseInfo={setCaseInfo}
                                            {...buildFieldPaths(fileKey, 'medical_chronology', itemIndex, 'patient_name')}
                                            setSnackBarMessage={setSnackBarMessage}
                                        />
                                    </Box>
                                )}

                                {/* Provider - use provider_name from providers array, old schema, or fallback to facility_name */}
                                {(() => {
                                    const hasProvidersArray = Array.isArray(item.providers) && item.providers.length > 0;
                                    const hasOldSchema = item.provider_name;
                                    const providerName = hasProvidersArray
                                        ? item.providers[0]?.provider_name
                                        : (hasOldSchema ? item.provider_name : null);
                                    const displayProviderName = providerName || item.facility_name;

                                    if (displayProviderName) {
                                        const usingFacilityAsProvider = !providerName && item.facility_name;
                                        return (
                                            <Box sx={{ mb: 0.5 }}>
                                                <Typography component="span" sx={{ fontWeight: 600 }}>Provider: </Typography>
                                                {hasProvidersArray && providerName ? (
                                                    <EditableFieldWithWarnings
                                                        onWarningClick={(e) => {
                                                            e.stopPropagation();
                                                            setDisplayedDocument({
                                                                'key': fileKey,
                                                                'page': sourcePage
                                                            });
                                                        }}
                                                        caseInfo={caseInfo}
                                                        setCaseInfo={setCaseInfo}
                                                        textPath={`$.documents['${fileKey}'].analysis.medical_chronology[${itemIndex}].providers[0].provider_name`}
                                                        needsReviewPath={buildArraySchemaWarningPath(`$.documents['${fileKey}'].analysis.medical_chronology[${itemIndex}].providers[0]`, 'provider_name', 'needs_review')}
                                                        caveatsPath={buildArraySchemaWarningPath(`$.documents['${fileKey}'].analysis.medical_chronology[${itemIndex}].providers[0]`, 'provider_name', 'caveats')}
                                                        setSnackBarMessage={setSnackBarMessage}
                                                    />
                                                ) : (
                                                    <EditableFieldWithWarnings
                                                        onWarningClick={(e) => {
                                                            e.stopPropagation();
                                                            setDisplayedDocument({
                                                                'key': fileKey,
                                                                'page': sourcePage
                                                            });
                                                        }}
                                                        caseInfo={caseInfo}
                                                        setCaseInfo={setCaseInfo}
                                                        {...buildFieldPaths(fileKey, 'medical_chronology', itemIndex, usingFacilityAsProvider ? 'facility_name' : 'provider_name')}
                                                        setSnackBarMessage={setSnackBarMessage}
                                                    />
                                                )}
                                            </Box>
                                        );
                                    }
                                    return null;
                                })()}

                                {/* Facility - only show if it exists and is different from provider */}
                                {(() => {
                                    const hasProvidersArray = Array.isArray(item.providers) && item.providers.length > 0;
                                    const hasOldSchema = item.provider_name;
                                    const providerName = hasProvidersArray
                                        ? item.providers[0]?.provider_name
                                        : (hasOldSchema ? item.provider_name : null);
                                    const shouldShowFacility = item.facility_name && providerName && item.facility_name !== providerName;

                                    if (shouldShowFacility) {
                                        return (
                                            <Box sx={{ mb: 0.5 }}>
                                                <Typography component="span" sx={{ fontWeight: 600 }}>Facility: </Typography>
                                                <EditableFieldWithWarnings
                                                    onWarningClick={(e) => {
                                                        e.stopPropagation();
                                                        setDisplayedDocument({
                                                            'key': fileKey,
                                                            'page': sourcePage
                                                        });
                                                    }}
                                                    caseInfo={caseInfo}
                                                    setCaseInfo={setCaseInfo}
                                                    {...buildFieldPaths(fileKey, 'medical_chronology', itemIndex, 'facility_name')}
                                                    setSnackBarMessage={setSnackBarMessage}
                                                />
                                            </Box>
                                        );
                                    }
                                    return null;
                                })()}
                            </Box>
                        </TableCell>
                        <TableCell sx={{
                            borderBottom: '1px solid var(--border-color)',
                        }}>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    title="Click to view source document"
                                    sx={{
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        fontSize: '0.9rem',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({
                                            'key': item['source_filekey'],
                                            'page': sourcePage
                                        });
                                    }}
                                >
                                    {filename} {sourcePage && `(page ${sourcePage})`}{item.source?.bates_number && `(${item.source?.bates_number})`}
                                </Typography>
                                <IconButton
                                    className="source-view-icon"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDisplayedDocument({
                                            'key': item['source_filekey'],
                                            'page': sourcePage
                                        });
                                    }}
                                    sx={{
                                        padding: '2px',
                                        visibility: 'hidden',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, visibility 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                    title="Click to view source document"
                                >
                                    <VisibilityIcon />
                                </IconButton>
                            </Box>
                        </TableCell>
                    </TableRow>
                );
            }

            return null;
        })
        .filter(row => row !== null);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2
            }}>
                <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Timeline" />
            </Box>
            <Table
                ref={tableRef}
                sx={{
                    minWidth: 650,
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
                aria-label="timeline events table"
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "150px"
                        }}>
                            Date
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Event
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Source Document
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableBodyRows}
                </TableBody>
            </Table>
        </Box>
    )
}
